import './style.css'
import ReactDOM from 'react-dom/client'
import React, { useRef, useEffect, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import Experience from './Experience.jsx'
import { CameraControls, PerspectiveCamera } from '@react-three/drei'
import { dragState } from './dragState.js';
import { QUALITY, DEBUG, DIAG } from './device.js';

const DEFAULT_POLAR = Math.PI / 2;
const FOV = 50;

// Under ?debug=1 only — lets the live interaction state be inspected from the
// console or an automated input test, without shipping a global in normal builds.
if (DEBUG) window.__dragState = dragState;

// ─── Responsive framing ──────────────────────────────────────────────────────
// A PerspectiveCamera's fov is VERTICAL, so a fixed camera distance crops harder
// and harder horizontally as the viewport narrows. On a portrait phone (aspect
// ~0.46) the model ended up spanning most of the screen width, and the particle
// shell around it fell largely behind the model where depth testing hides it.
//
// So the distance is derived from the model's own radius and the viewport aspect
// instead of being hard-coded. On a narrow screen the binding constraint is the
// horizontal half-extent (d * tan(fov/2) * aspect); on a wide one it is the
// vertical (d * tan(fov/2)).
// 0.46 is back-solved from the original hard-coded distance of 5 at the model's
// measured radius, so wide viewports keep exactly the framing they always had.
const FILL_WIDE   = 0.46;
const FILL_NARROW = 0.58;  // a phone can carry a relatively larger hero

function computeFraming(radius, w, h) {
    const aspect  = (w > 0 && h > 0) ? w / h : 1;
    const halfFov = (FOV * Math.PI / 180) / 2;
    const bind    = Math.min(aspect, 1);
    // 0 at aspect >= 1, ramping to 1 by aspect 0.5
    const t    = Math.min(1, Math.max(0, (1 - aspect) / 0.5));
    const fill = FILL_WIDE + (FILL_NARROW - FILL_WIDE) * t;
    const dist = radius / (fill * Math.tan(halfFov) * bind);
    return Math.max(4.2, Math.min(9.5, dist));
}

// Owns cursor every frame at priority 100 — runs after CameraControls (priority 0)
// so our write always wins over its gl.domElement.style.cursor = 'grab' etc.
function CursorSync() {
    const { gl, camera, size, viewport } = useThree();
    useFrame(() => {
        const cur = (dragState.hovering || dragState.active) ? 'none' : '';
        document.documentElement.style.cursor = cur;
        gl.domElement.style.cursor = cur;
        if (DEBUG) {
            DIAG.camDist = Math.round(camera.position.length() * 100) / 100;
            DIAG.aspect  = Math.round((size.width / size.height) * 100) / 100;
            DIAG.dpr     = viewport.dpr;
            DIAG.counts  = `${QUALITY.starCount}/${QUALITY.galaxyCount} r=${dragState.modelRadius.toFixed(2)}`;
        }
    }, 100);
    return null;
}

// Sits inside Canvas — smoothly drifts polar angle back to equatorial after drag
function PolarAutoReturn({ controlsRef }) {
    const polarVelRef = useRef(0); // spring velocity accumulator
    useFrame((state, delta) => {
        if (!controlsRef.current) return;
        controlsRef.current.polarRotateSpeed = (dragState.hovering || dragState.active) ? 0.8 : 0;
        if (dragState.active) return;
        if (dragState.hovering) {
            polarVelRef.current = 0; // reset spring while hovering
            return;
        }

        const polar = controlsRef.current.polarAngle;
        const diff  = polar - DEFAULT_POLAR;
        if (Math.abs(diff) < 0.0003 && Math.abs(polarVelRef.current) < 0.0003) {
            polarVelRef.current = 0;
            return;
        }

        // Spring: pull toward DEFAULT_POLAR, with damping — springy ease-out
        const springK = 5.0;  // stiffness
        const dampK   = 7.0;  // damping (>2*sqrt(springK) = overdamped, <that = bouncy)
        polarVelRef.current += (-diff * springK - polarVelRef.current * dampK) * delta;
        controlsRef.current.rotatePolarTo(polar + polarVelRef.current * delta, false);
    });

    return null;
}

// ─── ?debug=1 readout ────────────────────────────────────────────────────────
// Prints what this device actually resolved to, on screen, so a phone can report
// its own capabilities instead of us inferring them from a description.
function DebugReadout() {
    const [, bump] = useState(0);
    const fpsRef = useRef(0);

    useEffect(() => {
        let raf, frames = 0, last = performance.now();
        const tick = () => {
            frames++;
            const now = performance.now();
            if (now - last >= 500) {
                fpsRef.current = Math.round((frames * 1000) / (now - last));
                frames = 0;
                last = now;
                bump(n => n + 1);
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, []);

    const rows = [
        ['fps', fpsRef.current],
        ['tier', DIAG.tier],
        ['mobile', String(DIAG.mobile)],
        ['cores / mem', `${DIAG.cores} / ${DIAG.memory}`],
        ['webgl2', String(DIAG.webgl2)],
        ['float-linear', String(DIAG.floatLinear)],
        ['lighting', DIAG.lighting || '—'],
        ['motion blur', String(DIAG.motionBlur)],
        ['context lost', String(DIAG.contextLost)],
        ['cam dist', DIAG.camDist],
        ['aspect / dpr', `${DIAG.aspect} / ${DIAG.dpr}`],
        ['stars/galaxy', DIAG.counts],
        ['label', DIAG.label],
        ['gpu', DIAG.gpu || '—'],
    ];

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, zIndex: 10,
            font: '11px/1.5 ui-monospace, SFMono-Regular, Menlo, monospace',
            color: '#9fe8c8', background: 'rgba(0,0,0,0.72)',
            padding: '8px 10px', maxWidth: '72vw', pointerEvents: 'none',
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
            {rows.map(([k, v]) => `${k.padEnd(13)}${v}`).join('\n')}
        </div>
    );
}

function App() {
    const [ready, setReady] = useState(false);
    const [framing, setFraming] = useState({ dist: 5, min: 4, max: 7 });
    const controlsRef = useRef();
    const dragDivRef = useRef();

    useEffect(() => {
        const handler = () => setReady(true);
        window.addEventListener('model-ready', handler);
        return () => window.removeEventListener('model-ready', handler);
    }, []);

    // Recomputed on load, resize and orientation change. Until the model has
    // parsed, dragState.modelRadius is 0 and the default framing stands.
    useEffect(() => {
        const recompute = () => {
            const r = dragState.modelRadius;
            if (!r) return;
            const dist = computeFraming(r, window.innerWidth, window.innerHeight);
            // Keep the same relative pinch-zoom range the fixed 4..7 gave around 5
            setFraming(prev => (Math.abs(prev.dist - dist) < 0.01
                ? prev
                : { dist, min: dist * 0.8, max: dist * 1.45 }));
        };
        recompute();
        window.addEventListener('model-ready', recompute);
        window.addEventListener('resize', recompute);
        window.addEventListener('orientationchange', recompute);
        return () => {
            window.removeEventListener('model-ready', recompute);
            window.removeEventListener('resize', recompute);
            window.removeEventListener('orientationchange', recompute);
        };
    }, []);

    // CameraControls owns camera.position every frame, so the camera has to be
    // dollied rather than repositioned.
    useEffect(() => {
        const c = controlsRef.current;
        if (!c) return;
        // Widen the clamp BEFORE dollying. CameraControls clamps dollyTo against
        // its *current* maxDistance, and the React prop carrying the new one has
        // not necessarily been applied yet this render — without this the camera
        // silently stops at the previous limit instead of the requested distance.
        c.minDistance = framing.min;
        c.maxDistance = framing.max;
        c.dollyTo(framing.dist, false);
    }, [framing]);

    // Drag handlers — horizontal feeds into model spin, vertical handled by CameraControls
    // Only activate when the drag starts on the model itself.
    const onPointerDown = (e) => {
        // Second finger during an active drag = pinch gesture — hand it to
        // CameraControls and stop spinning. Checked before the hit test so it works
        // wherever that finger lands, not just on the model, and the first finger's
        // capture is released so the two-pointer gesture is not fought over.
        if (dragState.active && e.pointerId !== dragState.pointerId) {
            if (dragState.pointerId !== null && e.currentTarget.hasPointerCapture?.(dragState.pointerId)) {
                e.currentTarget.releasePointerCapture(dragState.pointerId);
            }
            dragState.active = false;
            dragState.velocity = 0;
            dragState.velocityY = 0;
            dragState.pointerId = null;
            return;
        }

        // Hit-test this press's own position. dragState.hovering reflects the last
        // pointer MOVE, which does not exist yet for the first touch of a gesture.
        dragState.ndcX = (e.clientX / window.innerWidth)  * 2 - 1;
        dragState.ndcY = -(e.clientY / window.innerHeight) * 2 + 1;
        const onModel = dragState.hitTest
            ? dragState.hitTest(dragState.ndcX, dragState.ndcY)
            : dragState.hovering;
        if (!onModel) return;

        // Grabbing the model counts as hovering it. On a mouse the raycast has
        // already set this; on a finger nothing has, and the drag is about to
        // suppress the raycast — so the label would otherwise never fade in.
        dragState.hovering = true;
        dragState.active = true;
        dragState.pointerId = e.pointerId;
        dragState.lastX = e.clientX;
        dragState.lastY = e.clientY;
        dragState.velocity = 0;
        dragState.velocityY = 0;
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e) => {
        // Always update NDC for hover raycast (even when not dragging)
        dragState.ndcX = (e.clientX / window.innerWidth)  * 2 - 1;
        dragState.ndcY = -(e.clientY / window.innerHeight) * 2 + 1;

        if (!dragState.active) return;
        if (e.pointerId !== dragState.pointerId) return;
        const dx = e.clientX - dragState.lastX;
        const dy = e.clientY - dragState.lastY;
        dragState.lastX = e.clientX;
        dragState.lastY = e.clientY;
        dragState.velocity  = (dx  / window.innerWidth)  * Math.PI * 2.5;
        dragState.velocityY = (dy  / window.innerHeight) * Math.PI * 2.0;
    };

    const onPointerLeave = () => {
        // Cursor left the browser window entirely — put NDC offscreen
        dragState.ndcX = 0;
        dragState.ndcY = -9;
    };

    const onPointerUp = () => {
        dragState.active = false;
        // Hover is recomputed by raycast on the very next frame — nothing else needed.
    };

    return (
        <>
            <div
                ref={dragDivRef}
                style={{ width: '100%', height: '100%', position: 'fixed', inset: 0, touchAction: 'none' }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onPointerLeave={onPointerLeave}
            >
                <Canvas
                    style={{ background: '#000' }}
                    dpr={QUALITY.dpr}
                    gl={{ antialias: QUALITY.antialias, powerPreference: 'default' }}
                    onCreated={({ gl }) => {
                        // A lost context is the one failure that looks exactly like
                        // "it just went black", so record it rather than leaving it
                        // to be guessed at. Mobile GPUs drop contexts under memory
                        // pressure; preventDefault lets the browser restore it.
                        gl.domElement.addEventListener('webglcontextlost', (e) => {
                            e.preventDefault();
                            DIAG.contextLost = true;
                            console.error('[gl] context lost');
                        });
                        gl.domElement.addEventListener('webglcontextrestored', () => {
                            console.warn('[gl] context restored');
                        });
                    }}
                >

                    <CameraControls
                        ref={controlsRef}
                        minPolarAngle={0}
                        maxPolarAngle={Math.PI / 1.3}
                        azimuthRotateSpeed={0}
                        polarRotateSpeed={0}
                        draggingSmoothTime={0.05}
                        minDistance={framing.min}
                        maxDistance={framing.max}
                        truckSpeed={0}
                        enableDamping={true}
                        dampingFactor={0.12}
                        smoothTime={0.5}
                    />
                    <PolarAutoReturn controlsRef={controlsRef} />
                    <CursorSync />
                    <Experience />
                    <PerspectiveCamera makeDefault position={[0, 0, framing.dist]} fov={FOV} />
                </Canvas>
            </div>
            {/* 1px line — loading indicator only */}
            <div className="preloader-track" style={{ opacity: ready ? 0 : 1 }}>
                <div className={`preloader-bar${ready ? ' done' : ''}`} />
            </div>
            {/* Full-screen overlay that fades out once model is loaded */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgb(0,0,0)',
                    pointerEvents: 'none',
                    transition: 'opacity 0.8s ease',
                    opacity: ready ? 0 : 1,
                }}
            />
            {DEBUG && <DebugReadout />}
        </>
    );
}

const root = ReactDOM.createRoot(document.querySelector('#root'))
root.render(<App />);
